// Identität und Konten der Shells aus dem Kern (@web/lib: useAuth, coreApi). Die Demo-Persona aus
// `?as=` meldet der Kern selbst an (Header x-bar-as), es gibt keinen Rückfall im Web.
import type { Me } from '@bar/contracts/core';
import { useQuery } from '@tanstack/react-query';
import { coreApi, coreKeys, useAuth } from '@web/lib';
import { useMemo } from 'react';
import type { ShellIdentity } from './identity';
import { navigateAway } from './navigation';

export type IdentityState =
  | { status: 'loading' }
  | { status: 'anonymous' }
  | { status: 'error' }
  | { status: 'ready'; identity: ShellIdentity };

export function identityFromMe(me: Me): ShellIdentity {
  return {
    accountId: me.account.id,
    employeeId: me.employee?.id ?? null,
    name: me.employee?.displayName ?? me.account.displayName,
    email: me.account.email,
    roles: me.roles,
    persona: me.persona,
    platforms: me.platforms,
    authMode: me.authMode,
  };
}

/** Wer angemeldet ist: aus /me, sonst anonym (Login-Gate) oder Fehler (Retry). */
export function useShellIdentity(): IdentityState {
  const { status, me } = useAuth();
  return useMemo<IdentityState>(() => {
    if (status === 'loading') return { status: 'loading' };
    if (status === 'error') return { status: 'error' };
    if (status === 'authenticated' && me) return { status: 'ready', identity: identityFromMe(me) };
    return { status: 'anonymous' };
  }, [status, me]);
}

export interface BrowserAccountItem {
  id: string;
  /** Person-ID für die Avatar-Farbe (wie Kopf und Personenseiten). */
  avatarId: string;
  name: string;
  email: string;
  active: boolean;
}

/** Angemeldete Konten dieses Browsers (Kontowechsel, Modus local/demo). */
export function useBrowserAccounts(identity: ShellIdentity): BrowserAccountItem[] {
  const query = useQuery({
    queryKey: coreKeys.browserAccounts,
    queryFn: coreApi.browserAccounts,
    retry: false,
    enabled: identity.authMode !== 'supabase',
    staleTime: 60_000,
  });
  return useMemo(() => {
    const list = query.data ?? [];
    if (list.length > 0)
      return list.map((a) => ({
        id: a.accountId,
        avatarId: a.employeeId ?? a.accountId,
        name: a.displayName,
        email: a.email,
        active: a.active,
      }));
    return [
      {
        id: identity.accountId,
        avatarId: identity.employeeId ?? identity.accountId,
        name: identity.name,
        email: identity.email,
        active: true,
      },
    ];
  }, [query.data, identity]);
}

/** Abmelden bzw. Konto wechseln über den Kern; danach volle Navigation (Registry, Viewer, Cache neu). */
export function useSessionActions() {
  const auth = useAuth();
  return useMemo(
    () => ({
      async signOut(all = false) {
        let remaining = null;
        try {
          remaining = await auth.logout({ all });
        } catch {
          // Auch bei Netzfehlern zur Anmeldung; die Sitzung läuft serverseitig ab.
        }
        // Ist im Browser noch ein anderes Konto angemeldet, geht es dort weiter (Kontowechsel K7).
        navigateAway(remaining ? '/' : '/login');
      },
      async switchAccount(accountId: string) {
        await auth.switchAccount(accountId);
        window.location.reload();
      },
    }),
    [auth],
  );
}
